import React from "react";
import { useSpring, animated, config } from "@react-spring/web";
import Chip from "./atoms/Chip";
import "./TabList.css";

export interface TabData<T> {
    id: T;
    label: string;
    count?: number;
}

interface TabListProps<T> {
    tabs: TabData<T>[];
    current: T;
    setCurrent: (name: T) => void;
    size?: "default" | "small";
}

const TabList = <T extends string>({
    tabs,
    current,
    setCurrent,
    size = "default",
}: TabListProps<T>) => {
    const activeTabIndex = tabs.findIndex((tab) => tab.id === current);
    const widthPercent = 100 / tabs.length;

    const springProps = useSpring({
        to: {
            left: `${widthPercent * activeTabIndex}%`,
            width: `${widthPercent}%`,
        },
        config: config.stiff,
    });

    return (
        <div
            className={`tab-list ${size === "small" ? "tab-list--small" : ""}`}
            role="tablist"
        >
            {tabs.map((tab, index) => {
                return (
                    <button
                        key={tab.id}
                        className={`tab-item ${activeTabIndex === index ? "active" : ""}`}
                        onClick={() => setCurrent(tab.id)}
                        role="tab"
                        aria-selected={activeTabIndex === index}
                        aria-controls={`panel-${tab.id}`}
                    >
                        <span>{tab.label}</span>
                        {tab.count != null && <Chip>{tab.count}</Chip>}
                    </button>
                );
            })}

            <animated.div className="active-underline" style={springProps} />
        </div>
    );
};

export default TabList;
